import storefront from 'lib/storefront';
import { metaobjects as metaobjectsQuery } from 'lib/graphql/queries';

/**
 * @typedef {Object} SamplePromotion
 * @property {string} id - Metaobject GID
 * @property {string} handle - Metaobject handle
 * @property {string} type - Metaobject type (sample_promotion)
 * @property {string} name - Promotion name
 * @property {number|string} threshold - Cart threshold to qualify
 * @property {number|string} quantity_allowed - Number of samples customer can select
 * @property {Array<{id: string, handle: string, title: string}>} products - Available sample products
 * @property {string} start_date - Promotion start date (ISO 8601)
 * @property {string} end_date - Promotion end date (ISO 8601)
 * @property {string|boolean} active - Whether promotion is active
 * @property {string} title - Display title
 * @property {string} copy - Display copy/description
 */

/**
 * @typedef {Object} GWPTier
 * @property {string} id - Metaobject GID
 * @property {string} name - Tier name/code
 * @property {string} threshold - Cart threshold in cents (converted from money field during parsing)
 * @property {number|string} limit - Max number of GWP items allowed
 * @property {Array<{id: string, variants: {edges: Array}}>} product_list - GWP gift products
 * @property {string} [select_label] - Modal heading when choosing qualifying products
 */

/**
 * @typedef {Object} GWPCollection
 * @property {string} id - Metaobject GID
 * @property {string} name - Collection promo name
 * @property {Array<{id: string, variants: {edges: Array}}>} qualifying_product_list - Products customers must buy from to qualify
 * @property {number|string} min_products_required - Minimum products to buy to qualify
 * @property {Array<{id: string, variants: {edges: Array}}>} product_list - Free gift products
 * @property {number|string} gift_limit - Max number of free gifts allowed
 * @property {string} promo_message - Full qualifying banner message (admin-configured)
 */

/**
 * @typedef {Object} GWPPromotion
 * @property {string} id - Metaobject GID
 * @property {string} handle - Metaobject handle
 * @property {string} type - Metaobject type (gwp_promotion)
 * @property {string} name - Promotion name
 * @property {GWPTier[]} tiers - List of GWP tiers (for tiered promotions)
 * @property {GWPCollection|null} collection - GWP collection config (for collection promotions)
 * @property {string} countries - Eligible countries (newline-separated)
 * @property {string} message - Promotional message
 * @property {string} start_date - Promotion start date (ISO 8601)
 * @property {string} end_date - Promotion end date (ISO 8601)
 * @property {string|boolean} active - Whether promotion is active
 * @property {string} exclusive_filter - Exclusive filter criteria
 * @property {{url: string, altText: string}|null} image - Promotional image
 * @property {string} text_color - Text color (hex/CSS)
 * @property {string} background_color - Background color (hex/CSS)
 * @property {string} button_background - Button background color (hex/CSS)
 * @property {string} button_text_color - Button text color (hex/CSS)
 */

/**
 * @typedef {Object} PromotionsData
 * @property {GWPPromotion[]} gwp - GWP promotions
 * @property {SamplePromotion[]} samples - Sample promotions
 */

/**
 * Parses a nested Metaobject (e.g., GWP Tier) into a flat object
 * @param {Object} metaobject - Nested metaobject from GraphQL response
 * @returns {Object} Parsed metaobject with flattened fields
 */
function parseNestedMetaobject(metaobject) {
  if (!metaobject?.fields) return metaobject;

  const parsed = {
    id: metaobject.id,
    handle: metaobject.handle,
    type: metaobject.type,
  };

  metaobject.fields.forEach(field => {
    if (field.references?.edges?.length) {
      parsed[field.key] = field.references.edges.map(edge => edge.node);
    } else if (field.reference) {
      parsed[field.key] = field.reference;
    } else if (field.type === 'money' && field.value) {
      try {
        const money = JSON.parse(field.value);
        parsed[field.key] = String(Math.round(parseFloat(money.amount) * 100));
      } catch {
        parsed[field.key] = field.value;
      }
    } else {
      parsed[field.key] = field.value;
    }
  });

  return parsed;
}

/**
 * Normalizes boolean-like string values to proper booleans
 * @param {any} value - Value to normalize
 * @returns {any} true/false for boolean strings, original value otherwise
 */
function normalizeValue(value) {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'true') return true;
    if (lower === 'false') return false;
  }
  return value;
}

/**
 * Parses metaobject fields into a flat object for easier consumption
 * @param {Array} fields - Array of field objects from metaobject
 * @returns {Object} Parsed fields as key-value pairs
 */
function parseMetaobjectFields(fields) {
  return fields.reduce((acc, field) => {
    if (field.reference?.image) {
      acc[field.key] = {
        url: field.reference.image.url,
        altText: field.reference.image.altText,
      };
    }
    else if (field.reference?.type && field.reference?.fields) {
      acc[field.key] = parseNestedMetaobject(field.reference);
    }
    else if (field.reference) {
      acc[field.key] = field.reference;
    }
    else if (field.references?.edges?.length) {
      acc[field.key] = field.references.edges.map(edge => {
        const node = edge.node;
        if (node.type && node.fields) {
          return parseNestedMetaobject(node);
        }
        return node;
      });
    }
    else if (field.type === 'json' && field.value) {
      try {
        acc[field.key] = JSON.parse(field.value);
      } catch {
        acc[field.key] = field.value;
      }
    }
    else if (field.type === 'money' && field.value) {
      try {
        const money = JSON.parse(field.value);
        acc[field.key] = String(Math.round(parseFloat(money.amount) * 100));
      } catch {
        acc[field.key] = field.value;
      }
    }
    else if (field.type === 'boolean') {
      acc[field.key] = normalizeValue(field.value);
    }
    else {
      acc[field.key] = normalizeValue(field.value);
    }
    return acc;
  }, {});
}

/**
 * Normalizes raw metaobject response into a cleaner structure
 * @param {Object} metaobject - Raw metaobject from GraphQL response
 * @returns {Object} Normalized metaobject
 */
function normalizeMetaobject(metaobject) {
  return {
    id: metaobject.id,
    handle: metaobject.handle,
    type: metaobject.type,
    ...parseMetaobjectFields(metaobject.fields),
  };
}

// ============================================================================
// FILTERING UTILITIES
// ============================================================================

/**
 * Gets the current buyer's country code from Shopify context
 * @returns {string} Two-letter country code (e.g., 'US', 'CA', 'GB')
 */
function getBuyerCountry() {
  return window.Shopify?.country || window.Shopify?.locale?.country || 'US';
}

/**
 * Checks if a promotion is currently within its date range.
 * Draft metaobjects are excluded by the Storefront API, so all fetched
 * promotions are implicitly active — only date range needs checking.
 * @param {Object} promotion - Promotion object with start_date, end_date fields
 * @returns {boolean} True if promotion is within date range
 */
function isPromotionActive(promotion) {
  const now = new Date();

  if (promotion.start_date) {
    const startDate = new Date(promotion.start_date);
    if (now < startDate) return false;
  }

  if (promotion.end_date) {
    const endDate = new Date(promotion.end_date);
    if (now > endDate) return false;
  }

  return true;
}

/**
 * Checks if a GWP promotion is available for the buyer's country
 * @param {GWPPromotion} promotion - GWP promotion object
 * @param {string} [buyerCountry] - Override buyer country (defaults to Shopify context)
 * @returns {boolean} True if promotion is available for the country
 */
function isPromotionAvailableForCountry(promotion, buyerCountry) {
  const country = buyerCountry || getBuyerCountry();

  if (!promotion.countries || promotion.countries.trim() === '') {
    return true;
  }

  const eligibleCountries = promotion.countries
    .split(/[\n,]/)
    .map(c => c.trim().toUpperCase())
    .filter(c => c.length > 0);

  if (eligibleCountries.includes('WORLD')) {
    return true;
  }

  return eligibleCountries.includes(country.toUpperCase());
}

/**
 * Filters promotions to only include active ones within date range
 * @param {Array<GWPPromotion|SamplePromotion>} promotions - Array of promotions
 * @returns {Array} Filtered active promotions
 */
export function filterActivePromotions(promotions) {
  return promotions.filter(isPromotionActive);
}

/**
 * Filters GWP promotions by buyer's country
 * @param {GWPPromotion[]} promotions - Array of GWP promotions
 * @param {string} [buyerCountry] - Override buyer country (defaults to Shopify context)
 * @returns {GWPPromotion[]} Filtered promotions available for country
 */
export function filterPromotionsByCountry(promotions, buyerCountry) {
  return promotions.filter(p => isPromotionAvailableForCountry(p, buyerCountry));
}

/**
 * Filters promotions by both active status and country eligibility
 * @param {GWPPromotion[]} promotions - Array of GWP promotions
 * @param {string} [buyerCountry] - Override buyer country (defaults to Shopify context)
 * @returns {GWPPromotion[]} Filtered eligible promotions
 */
export function filterEligibleGWPPromotions(promotions, buyerCountry) {
  return promotions
    .filter(isPromotionActive)
    .filter(p => isPromotionAvailableForCountry(p, buyerCountry));
}

/**
 * Filters sample promotions by active status
 * @param {SamplePromotion[]} promotions - Array of sample promotions
 * @returns {SamplePromotion[]} Filtered active sample promotions
 */
export function filterEligibleSamplePromotions(promotions) {
  return promotions.filter(isPromotionActive);
}

// ============================================================================
// FETCH FUNCTIONS
// ============================================================================

/**
 * Fetches all GWP (Gift With Purchase) promotion metaobjects
 * @param {number} first - Number of metaobjects to fetch (default: 50)
 * @returns {Promise<GWPPromotion[]>} Array of GWP promotion metaobjects
 */
export async function fetchGWPPromotions(first = 50) {
  try {
    const response = await storefront.request(metaobjectsQuery, {
      type: 'gwp_promotion',
      first,
    });

    const metaobjects = response?.metaobjects?.edges ?? [];
    const normalized = metaobjects.map(edge => normalizeMetaobject(edge.node));

    return normalized;
  } catch (error) {
    console.error('[Promotions API] Error fetching GWP promotions:', error);
    return [];
  }
}

/**
 * Fetches all Sample promotion metaobjects
 * @param {number} first - Number of metaobjects to fetch (default: 50)
 * @returns {Promise<SamplePromotion[]>} Array of Sample promotion metaobjects
 */
export async function fetchSamplePromotions(first = 50) {
  try {
    const response = await storefront.request(metaobjectsQuery, {
      type: 'sample_promotion',
      first,
    });

    const metaobjects = response?.metaobjects?.edges ?? [];
    return metaobjects.map(edge => normalizeMetaobject(edge.node));
  } catch (error) {
    console.error('[Promotions API] Error fetching Sample promotions:', error);
    return [];
  }
}

/**
 * Fetches all promotions (GWP and Sample) in parallel (unfiltered)
 * @returns {Promise<PromotionsData>} Object containing both promotion types
 */
export async function fetchAllPromotions() {
  const [gwp, samples] = await Promise.all([
    fetchGWPPromotions(),
    fetchSamplePromotions(),
  ]);

  return { gwp, samples };
}

/**
 * Fetches only eligible promotions (active, within date range, country-filtered for GWP)
 * @param {string} [buyerCountry] - Override buyer country (defaults to Shopify context)
 * @returns {Promise<PromotionsData>} Object containing filtered promotion types
 */
export async function fetchEligiblePromotions() {
  const buyerCountry = getBuyerCountry();
  const { gwp, samples } = await fetchAllPromotions();

  const filteredGwp = filterEligibleGWPPromotions(gwp, buyerCountry);

  return {
    gwp: filteredGwp,
    samples: filterEligibleSamplePromotions(samples),
  };
}

// Cache for promotions data
let promotionsCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * @typedef {Object} FetchPromotionsOptions
 * @property {boolean} [forceRefresh=false] - Force a fresh fetch ignoring cache
 * @property {boolean} [includeInactive=false] - Include inactive/expired promotions
 * @property {string} [buyerCountry] - Override buyer country for filtering
 */

/**
 * Fetches all promotions with caching to avoid redundant API calls.
 * By default, returns only eligible promotions (active, within date range, country-filtered).
 * @param {FetchPromotionsOptions} [options={}] - Fetch options
 * @returns {Promise<PromotionsData>} Cached or fresh promotions data
 */
export async function fetchPromotionsOnCartOpen(options = {}) {
  const {
    forceRefresh = false,
    includeInactive = false,
    buyerCountry,
  } = options;

  const now = Date.now();

  if (
    !forceRefresh &&
    promotionsCache &&
    cacheTimestamp &&
    now - cacheTimestamp < CACHE_TTL
  ) {
    if (includeInactive) {
      return promotionsCache;
    }
    return {
      gwp: filterEligibleGWPPromotions(promotionsCache.gwp, buyerCountry),
      samples: filterEligibleSamplePromotions(promotionsCache.samples),
    };
  }

  promotionsCache = await fetchAllPromotions();
  cacheTimestamp = now;

  if (includeInactive) {
    return promotionsCache;
  }

  return {
    gwp: filterEligibleGWPPromotions(promotionsCache.gwp, buyerCountry),
    samples: filterEligibleSamplePromotions(promotionsCache.samples),
  };
}

/**
 * Clears the promotions cache
 */
export function clearPromotionsCache() {
  promotionsCache = null;
  cacheTimestamp = null;
}

export default {
  fetchGWPPromotions,
  fetchSamplePromotions,
  fetchAllPromotions,
  fetchEligiblePromotions,
  fetchPromotionsOnCartOpen,
  filterActivePromotions,
  filterPromotionsByCountry,
  filterEligibleGWPPromotions,
  filterEligibleSamplePromotions,
  clearPromotionsCache,
};

